import classNames from "classnames";
import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useState } from "react";
import { LiaCheckSolid, LiaTimesSolid } from "react-icons/lia";

import { useFormContext } from "react-hook-form";
import { ErrorMessage } from "../../../utils/ErrorMessage";
import { passwordValidator } from "../../../utils/password-validator";

type Inputprops = {
  label?: React.ReactNode;
  isNewPassword?: boolean;
} & JSX.IntrinsicElements["input"];

const PasswordInputField = forwardRef<HTMLInputElement, Inputprops>(
  function PasswordField(props, ref) {
    const {
      label,
      id,
      className,
      isNewPassword,
      value = "",
      ...restProps
    } = props;
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const method = useFormContext();

    if (!method) return null;
    const { formState } = method;

    const error = formState.errors[props.name as string];

    const passwordStrength = passwordValidator(value as string);
    const validationRules = [
      { key: "eightCharsOrGreater", label: "8 characters length" },
      { key: "lowercase", label: "1 letter" },
      { key: "number", label: "1 number" },
      { key: "specialChar", label: "1 special character" },
      { key: "uppercase", label: "1 uppercase letter" },
    ];

    const getPasswordStrength = () => {
      const validCount = Object.values(passwordStrength).filter(
        (rule) => rule.validator
      ).length;

      if (validCount === 0) return "";
      if (validCount <= 2) return "Weak password";
      if (validCount <= 4) return "Average password";
      return "Strong password";
    };

    const getPasswordStrengthColor = () => {
      const strength = getPasswordStrength();
      if (strength === "Weak password") return "text-[#E21414]";
      if (strength === "Average password") return "text-[#E09C00]";
      if (strength === "Strong password") return "text-[#4CAF50]";
      return "";
    };

    const getPasswordStrengthBar = () => {
      const validCount = Object.values(passwordStrength).filter(
        (rule) => rule.validator
      ).length;

      return (
        <div className="flex gap-1 mt-2">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className={`h-[8px] flex-1 rounded-full transition-all duration-300 ${
                validCount <= 2
                  ? index === 0
                    ? "bg-[#E21414]"
                    : "bg-[#F8F8F8]"
                  : validCount <= 4
                  ? index <= 1
                    ? "bg-[#FFB200]"
                    : "bg-[#F8F8F8]"
                  : "bg-[#00AD4B]"
              }`}
            />
          ))}
        </div>
      );
    };

    return (
      <div
        className={classNames(
          "flex flex-col",
          isNewPassword && !error?.message && "gap-[16px]"
        )}
      >
        <div className="relative">
          <label htmlFor={id} className="sr-only">
            {label}
          </label>
          <input
            {...restProps}
            id={id}
            type={showPassword ? "text" : "password"}
            className={`${styles.inputItem} ${className}`}
            aria-label={typeof label === "string" ? label : undefined}
            ref={ref}
            value={value}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              if (!e.relatedTarget?.classList.contains("show-password-btn")) {
                setIsFocused(false);
              }
            }}
          />

          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 show-password-btn"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <EyeOff color="#9ca3af" className="h-5 w-5" />
            ) : (
              <Eye color="#9ca3af" className="h-5 w-5" />
            )}
          </button>
        </div>

        {isNewPassword ? (
          <>
            {error?.message ? (
              <ErrorMessage fieldName={props.name || ""} />
            ) : (
              <div className="font-inter font-normal">
                {value && (
                  <div className="mb-[16px]">
                    <div
                      className={`text-[14px] leading-[17.78px] ${getPasswordStrengthColor()}`}
                    >
                      {getPasswordStrength()}
                    </div>
                    {getPasswordStrengthBar()}
                  </div>
                )}

                {(isFocused || value) && (
                  <div className="space-y-[8px]  font-inter">
                    {validationRules.map((rule) => {
                      const isValid =
                        passwordStrength[
                          rule.key as keyof typeof passwordStrength
                        ].validator;
                      return (
                        <div
                          key={rule.key}
                          className="flex items-center gap-2 text-[14px] leading-[17.78px] text-[#616161]"
                        >
                          {isValid ? (
                            <LiaCheckSolid className="text-[#00AD4B]" />
                          ) : (
                            <LiaTimesSolid className="text-[#616161]" />
                          )}
                          <span className={isValid ? "line-through" : ""}>
                            {rule.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <ErrorMessage fieldName={props.name || ""} />
        )}
      </div>
    );
  }
);

const styles = {
  inputItem:
    "w-full px-[12px] py-[14px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-black bg-[#F8F8F8]",
};

export default PasswordInputField;
